/**
 * Simple Modal Utility
 *
 * A reusable utility for handling modal display logic:
 * - Shows the modal twice for every user
 * - After showing twice, doesn't show again
 * - After 2 days, resets and starts showing again
 */

// Cookie helper functions
function setCookie(name, value, days) {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + JSON.stringify(value) + expires + "; path=/";
}

function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      try {
        return JSON.parse(c.substring(nameEQ.length, c.length));
      } catch (e) {
        return null;
      }
    }
  }
  return null;
}

/**
 * Simple Modal Manager
 * @param {Object} options - Configuration options
 * @param {string} options.modalId - ID of the modal element
 * @param {string} options.closeSelector - CSS selector for close button
 * @param {number} options.cookieExpiry - Cookie expiry in days
 * @param {number} options.resetDays - Days after which to reset the modal count
 * @param {boolean} options.closeOnOverlay - Whether to close when clicking overlay
 * @param {Function} options.onShow - Callback when modal is shown
 * @param {Function} options.onHide - Callback when modal is hidden
 */
export default function SimpleModalManager(options = {}) {
  const config = {
    modalId: "site-wide-modal",
    closeSelector: ".simple-modal__close",
    cookieExpiry: 30, // Cookie expiry in days
    resetDays: 2, // Reset after 2 days
    closeOnOverlay: true,
    onShow: null,
    onHide: null,
    ...options,
  };

  const delayTime = 3000; // mili seconds

  // Get modal element
  const modal = document.getElementById(config.modalId);
  if (!modal) {
    console.error(`Modal with ID "${config.modalId}" not found`);
    return null;
  }

  // Get close button
  const closeButton = modal.querySelector(config.closeSelector);

  // Show the modal
  function showModal() {
    modal.style.display = "block";
    // Trigger reflow to ensure transition works
    modal.offsetHeight;
    modal.classList.add("active");

    if (typeof config.onShow === "function") {
      config.onShow();
    }
  }

  // Hide the modal
  function hideModal() {
    modal.classList.remove("active");
    setTimeout(() => {
      modal.style.display = "none";

      if (typeof config.onHide === "function") {
        config.onHide();
      }
    }, 300); // Match this with your CSS transition time
  }

  // Initialize modal events
  function initEvents() {
    // Add event listener to close button
    if (closeButton) {
      closeButton.addEventListener("click", hideModal);
    }

    // Close when clicking overlay if enabled
    if (config.closeOnOverlay) {
      const overlay = modal.querySelector(".simple-modal__overlay");
      if (overlay) {
        overlay.addEventListener("click", hideModal);
      }
    }
  }

  // Check if modal should be shown based on visit logic
  function checkAndShowModal() {
    const cookieName = `modal_visitor_data_${config.modalId}`;

    // Get visitor data from cookie or create new data
    const visitorData = getCookie(cookieName) || {
      visitCount: 0,
      lastVisit: new Date().toISOString(),
    };

    const now = new Date();
    const lastVisit = new Date(visitorData.lastVisit);

    // Calculate days since last visit
    const daysSinceLastVisit = Math.floor(
      (now - lastVisit) / (1000 * 60 * 60 * 24)
    );

    // Check if we should reset the counter (if it's been more than resetDays since last visit)
    if (daysSinceLastVisit >= config.resetDays) {
      // Reset visit count
      visitorData.visitCount = 0;
    }

    // Check if we should show the modal (only if visit count is less than 2)
    if (visitorData.visitCount < 2) {
      // Show the modal
      setTimeout(() => {
        showModal();
      }, delayTime);

      // Increment visit count
      visitorData.visitCount++;

      // Update last visit time
      visitorData.lastVisit = now.toISOString();

      // Save updated data
      setCookie(cookieName, visitorData, config.cookieExpiry);
    }
    // If visit count is already 2 or more, don't show modal
    // But still update the last visit time to track when to reset
    else if (daysSinceLastVisit < config.resetDays) {
      // Just update the last visit time without incrementing count
      visitorData.lastVisit = now.toISOString();
      setCookie(cookieName, visitorData, config.cookieExpiry);
    }
  }

  // Reset modal state (for testing)
  function resetModalState() {
    document.cookie = `modal_visitor_data_${config.modalId}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  // Initialize events
  initEvents();

  // Return public methods
  return {
    show: showModal,
    hide: hideModal,
    check: checkAndShowModal,
    reset: resetModalState,
  };
}

// Make SimpleModalManager available globally
// window.SimpleModalManager = SimpleModalManager;
